import React, { useMemo, useState } from 'react';
import { Button } from '@rocco/components/button';
import { useWebsiteReferralTracking } from '~/modules/tracking/util/websiteTracking';
import type { ProjectPageLoader } from '@modules/project/type';
import type { LocationDataType } from '~/modules/professional/type/contact-page-types';
import {
    formatNumberWithCommas,
    formatTimeSpent,
    shortenCount,
} from '@rocco/utils/format-number';
import {
    EyeIcon,
    BookmarkIcon,
    BookOpenIcon,
    MessageCircleIcon,
    InfoIcon,
    MapPinIcon,
    ExternalLinkIcon,
} from 'lucide-react';
import { FollowSelector } from '~/modules/moodboards/components';
import { useHandleFollow } from '~/modules-rocco/design-board/hook/use-handle-follow';
import { useEnquiryModal } from '~/modules/enquiry/hooks/use-enquiry-modal';
import { useProjectBookMeeting } from '~/modules/project/hook/use-project-book-meeting';
import { cn } from '@rocco/utils/cn';
import { useNavigate } from 'react-router';
import { PROFESSIONAL_SECTIONS_ANCHOR } from '~/modules/professional/util/constants';
import { SpecifierLoginPrompt } from '~/modules/enquiry/component/specifier-login-prompt/SpecifierLoginPrompt';

/**
 * Professional information interface
 */
interface ProfessionalInfoData {
    Professional: ProjectPageLoader['project']['Professional'];
    ClientDirectorCalendarSession: ProjectPageLoader['project']['ClientDirectorCalendarSession'];
}

/**
 * Props for the ProfessionalInfo component
 */
interface ProfessionalInfoProps {
    /** Professional data containing company information */
    data: ProfessionalInfoData;
    /** Project ID for saving to design board and project-scoped enquiries */
    projectId: number;
    showEnquiryCTA?: boolean;
    showSpecifierLoginPrompt?: boolean;
}

/**
 * AggregatedPublicKPI type (temporary until GraphQL types are regenerated)
 */
interface AggregatedPublicKPI {
    ID: number;
    TotalPageViews?: number | null;
    TotalReferrals?: number | null;
    TotalSaves?: number | null;
    TotalEnquiries?: number | null;
    TotalImpressions?: number | null;
    TotalTimeSpent?: number | null;
    LibraryItemsCount?: number | null;
}

/**
 * Custom PublicKPI component that works with Professional AggregatedPublicKPI data
 */
const ProfessionalPublicKPI: React.FC<{
    aggregatedPublicKPI: AggregatedPublicKPI;
    professionalTitle: string;
}> = ({ aggregatedPublicKPI, professionalTitle }) => {
    const { TotalPageViews, TotalSaves, TotalImpressions, TotalTimeSpent } =
        aggregatedPublicKPI;
    const totalViews = (TotalPageViews ?? 0) + (TotalImpressions ?? 0);
    const formattedTotalViews =
        totalViews > 100 ? shortenCount(totalViews ?? 0) : null;

    const totalSaves = TotalSaves ?? 0;
    const formattedTotalSaves =
        totalSaves > 100 ? shortenCount(totalSaves ?? 0) : null;

    const totalTimeSpent = TotalTimeSpent ?? 0;
    const formattedTotalTimeSpent =
        totalTimeSpent > 100 ? formatTimeSpent(totalTimeSpent ?? 0) : null;

    const availableMetrics = [
        {
            name: 'Total Views',
            value: formattedTotalViews,
            icon: <EyeIcon className="size-5" />,
            shouldShow: formattedTotalViews !== null,
            title: `${formatNumberWithCommas(totalViews)} people viewed this profile.`,
        },
        {
            name: 'Total Saves',
            value: formattedTotalSaves,
            icon: <BookmarkIcon className="size-5" />,
            shouldShow: formattedTotalSaves !== null,
            title: `${professionalTitle} was saved by ${formatNumberWithCommas(totalSaves)} people.`,
        },
        {
            name: 'Total Time Spent',
            value: formattedTotalTimeSpent,
            icon: <BookOpenIcon className="size-5" />,
            shouldShow: formattedTotalTimeSpent !== null,
            title: `Together, ArchiPro users spent ${Math.floor(totalTimeSpent / 3600)} hours reading this profile.`,
        },
    ];

    return (
        <div className="flex flex-row gap-2">
            {availableMetrics.map((metric) => {
                if (!metric.shouldShow) return null;
                return (
                    <Button
                        key={metric.name}
                        color="secondary"
                        weight="normal"
                        title={metric.title}
                        className={'shrink-0 cursor-default gap-3'}
                    >
                        {metric.icon}
                        {metric.value}
                    </Button>
                );
            })}
        </div>
    );
};

/**
 * Component displaying professional information in a 2-column layout
 * Left column: Professional details and KPIs
 * Right column: Action buttons (Follow & Message)
 */
export const ProfessionalInfo: React.FC<ProfessionalInfoProps> = ({
    data,
    projectId,
    showEnquiryCTA = true,
    showSpecifierLoginPrompt = false,
}) => {
    const { Professional, ClientDirectorCalendarSession } = data;

    // Design board functionality (Save Project)
    const { isInDesignBoard } = useHandleFollow();
    const [optimisticFollowed, setOptimisticFollowed] = useState(false);
    const isSaved = optimisticFollowed || isInDesignBoard(projectId);

    // Book a Meeting (Client Director session) functionality
    const { canBookDirect, openBookingModal, bookingModal } =
        useProjectBookMeeting(ClientDirectorCalendarSession);

    // Enquiry modal functionality (Message) — scoped to the professional.
    const messageLabel = 'Message';
    const { openEnquiryModal } = useEnquiryModal({
        title: messageLabel,
        professional: Professional,
        itemType: 'professional',
        siteTreeID: Professional.ID,
        source: 'ProjectAboutProfessional',
        ctaLabel: messageLabel,
        itemTitle: Professional.Title,
        includeBranch: true,
    });

    // Memoized website hostname parsing for performance
    const websiteHostname = useMemo(() => {
        if (!Professional.Website) return null;

        try {
            const url = new URL(
                Professional.Website.startsWith('http')
                    ? Professional.Website
                    : `https://${Professional.Website}`,
            );
            return url.hostname.replace(/^www\./, '');
        } catch {
            return Professional.Website.replace(
                /^https?:\/\/(www\.)?/,
                '',
            ).split(/[/?#]/)[0];
        }
    }, [Professional.Website]);

    const { referralTrackingURL, referralTrackingParams } =
        useWebsiteReferralTracking(Professional.Website, {
            libraryItemID: Professional.ID,
            professionalID: Professional.ID,
            professionalTitle: Professional.Title,
            source: 'ProjectAboutProfessional',
        });

    const navigate = useNavigate();
    const contactUrl = `/professional/${Professional.URLSegment}/contact`;

    const handleLocationClick = (locationId?: number) => {
        const params = new URLSearchParams({ expandAddress: 'true' });
        if (locationId) {
            params.set('ID', locationId.toString());
        }
        const contactUrlWithExpand = `${contactUrl}?${params.toString()}#${PROFESSIONAL_SECTIONS_ANCHOR}`;
        navigate(contactUrlWithExpand);
    };

    return (
        <div className="flex gap-8">
            {/* Left Column - Professional Details & KPIs */}
            <div className="flex flex-1 flex-col gap-4 text-[#141414]">
                {/* Professional Basic Information */}
                <div className="flex flex-col">
                    <h2 className="mb-6 ml-2 text-7 leading-none font-medium tracking-[-0.08rem]">
                        {Professional.Title}
                    </h2>

                    {Professional.Categories?.[0]?.PrettyTitle && (
                        <div className="flex items-center gap-2 font-display text-base font-medium">
                            <InfoIcon className="h-4 w-4" />
                            <span>
                                {Professional.Categories[0].PrettyTitle}
                            </span>
                        </div>
                    )}

                    {Professional.LibraryLocations &&
                        Professional.LibraryLocations.length > 0 && (
                            <div className="mt-2.5 flex items-center gap-2 font-display text-base font-medium text-[#141414]">
                                <MapPinIcon className="h-4 w-4" />
                                {(() => {
                                    const locations =
                                        Professional.LibraryLocations as LocationDataType[];
                                    const firstLocation = locations[0];
                                    const remaining = locations.length - 1;

                                    // Build the display text for the first location
                                    const suburb = firstLocation?.Suburb;
                                    const city = firstLocation?.City;
                                    const region = firstLocation?.Region;
                                    const parts: string[] = [];
                                    if (suburb && city)
                                        parts.push(`${suburb}, ${city}`);
                                    else if (city) parts.push(city);
                                    else if (suburb) parts.push(suburb);
                                    if (region && region !== city)
                                        parts.push(region);
                                    const firstLocationText = parts.join(', ');

                                    return (
                                        <>
                                            <div
                                                onClick={() =>
                                                    handleLocationClick(
                                                        firstLocation?.ID,
                                                    )
                                                }
                                                className="cursor-pointer underline hover:opacity-80"
                                            >
                                                {firstLocationText || 'Address'}
                                            </div>
                                            {remaining > 0 && (
                                                <div
                                                    onClick={() =>
                                                        handleLocationClick(
                                                            firstLocation?.ID,
                                                        )
                                                    }
                                                    className="cursor-pointer hover:opacity-80"
                                                >
                                                    {`(+${remaining} More)`}
                                                </div>
                                            )}
                                        </>
                                    );
                                })()}
                            </div>
                        )}

                    {referralTrackingURL && (
                        <a
                            href={referralTrackingURL}
                            target="_blank"
                            {...referralTrackingParams}
                            rel="noopener noreferrer"
                            className="mt-2.5 flex items-center gap-2 font-display text-base font-medium text-[#141414]"
                        >
                            <ExternalLinkIcon className="h-4 w-4" />
                            <span className="underline hover:opacity-80">
                                {websiteHostname}
                            </span>
                        </a>
                    )}
                </div>

                {/* Public KPI Section */}
                {Professional.AggregatedPublicKPI && (
                    <div className="mt-4">
                        <ProfessionalPublicKPI
                            aggregatedPublicKPI={
                                Professional.AggregatedPublicKPI
                            }
                            professionalTitle={Professional.Title}
                        />
                    </div>
                )}
            </div>

            {/* Right Column - Action Buttons */}
            <div className="flex flex-col gap-2">
                {/* Save Project Button */}
                <FollowSelector
                    trigger={
                        <Button color="dark" className="w-77.5 px-6.5 py-3">
                            <span className="flex items-center gap-2">
                                {isSaved ? 'FOLLOWED' : 'FOLLOW'}
                                <BookmarkIcon
                                    className={cn(
                                        'w-4',
                                        isSaved ? 'fill-white' : 'stroke-white',
                                    )}
                                />
                            </span>
                        </Button>
                    }
                    pinId={projectId}
                    pinType="ProjectPin"
                    onFollowChange={setOptimisticFollowed}
                />

                {showSpecifierLoginPrompt && (
                    <SpecifierLoginPrompt
                        professionalName={Professional.Title}
                        itemDescription="this project"
                        showBottomBorder={false}
                        className="py-0"
                    />
                )}
                {canBookDirect && (
                    <Button
                        color="dark"
                        className="w-77.5 px-6.5 py-3 uppercase"
                        onClick={() => openBookingModal()}
                    >
                        Book a Meeting
                    </Button>
                )}
                {showEnquiryCTA && (
                    <Button
                        color="white"
                        className="w-77.5 border border-black px-6.5 py-3 uppercase hover:border-black"
                        onClick={() =>
                            openEnquiryModal({
                                title: messageLabel,
                                ctaLabel: messageLabel,
                            })
                        }
                    >
                        {messageLabel}
                        <MessageCircleIcon className="ml-2 w-4" />
                    </Button>
                )}
            </div>
            {bookingModal}
        </div>
    );
};
